import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ToggleLeft, ToggleRight, Settings2 } from "lucide-react";

/**
 * iter86.74 — Custom Template Features governance card (Admin Ops → Templates).
 *
 * Replaces the per-rivalry Features dropdown that used to live on the
 * Rivalries tab. Site admin flips a feature ON/OFF ONCE here; league admins
 * then self-select the enabled features inside the custom tournament wizard
 * (saved as per-league flags). Wire format: site_settings.disabled_custom_features.
 */
const FEATURE_TILES = [
    { key: "gotsport_enabled", icon: "🔄", label: "GotSport Sync", desc: "Pull teams + schedules from a GotSport event. League admin wires the event under Tournament Admin → Data Feed after creation." },
    { key: "feed_csv_enabled", icon: "📄", label: "CSV Upload", desc: "League admin imports teams + results by uploading CSV files." },
    { key: "portfolio_mode_unlocked", icon: "💼", label: "Portfolio Draft", desc: "Unlocks the portfolio-style draft mode on custom tournaments for free-tier rivalries." },
];

export default function CustomFeaturesCard() {
    const [disabled, setDisabled] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.getSiteSettings();
            setDisabled(Array.isArray(r?.disabled_custom_features) ? r.disabled_custom_features : []);
        } catch { /* defaults (all enabled) drive the render */ }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (tile) => {
        const isOff = disabled.includes(tile.key);
        const next = isOff ? disabled.filter((k) => k !== tile.key) : [...disabled, tile.key];
        setSaving(tile.key);
        try {
            const r = await api.updateSiteSettings({ disabled_custom_features: next });
            setDisabled(Array.isArray(r?.disabled_custom_features) ? r.disabled_custom_features : next);
            toast.success(isOff
                ? `${tile.label} enabled — league admins can select it in the custom tournament wizard`
                : `${tile.label} disabled — hidden from new custom tournaments`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || `${tile.label} toggle failed`);
        }
        setSaving(null);
    };

    return (
        <div className="brutal-card p-5 space-y-3" data-testid="custom-features-card">
            <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-cyber" />
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wider">Custom Template Features</h3>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Governs which data feeds and modes league admins can pick when creating a{" "}
                <strong className="text-white">custom</strong> tournament. Enabled features appear as
                options in their setup wizard (selections save as rivalry-wide flags); disabled features
                are hidden entirely. Existing rivalries that already turned a feature on are unaffected.
            </p>
            <div className="grid sm:grid-cols-2 gap-2 pt-2">
                {FEATURE_TILES.map((t) => {
                    const isOff = disabled.includes(t.key);
                    return (
                        <button
                            key={t.key}
                            type="button"
                            data-testid={`custom-feature-${t.key}-btn`}
                            onClick={() => toggle(t)}
                            disabled={saving === t.key}
                            className={`text-left p-3 border-2 rounded-md transition-all flex items-start gap-3 ${
                                isOff
                                    ? "border-[#333] bg-ink/40 opacity-70 hover:opacity-90"
                                    : "border-cyber/40 bg-cyber/5 hover:border-cyber"
                            } disabled:opacity-50`}
                        >
                            <div className="text-2xl shrink-0">{t.icon}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                    <span className="font-bold text-sm">{t.label}</span>
                                    {isOff
                                        ? <ToggleLeft className="w-4 h-4 text-zinc-500 shrink-0" />
                                        : <ToggleRight className="w-4 h-4 text-cyber shrink-0" />}
                                </div>
                                <p className="text-[10px] text-zinc-500 leading-relaxed">{t.desc}</p>
                                <p
                                    data-testid={`custom-feature-${t.key}-status`}
                                    className={`text-[10px] mt-1 font-bold uppercase tracking-wider ${isOff ? "text-heat" : "text-cyber"}`}
                                >
                                    {isOff ? "Disabled" : "Enabled"}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
            {loading && <p className="text-xs text-zinc-500">Loading...</p>}
        </div>
    );
}
